import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";

// Pages
import MainLandingPage from "@/pages/MainLandingPage";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import EnrollPage from "@/pages/EnrollPage";
import DashboardPage from "@/pages/DashboardPage";
import RecoveryBoardPage from "@/pages/RecoveryBoardPage";
import PaginatedChapterPage from "@/pages/PaginatedChapterPage";
import { useParams } from 'react-router-dom';

// Wrapper forces full remount when chapter ID changes — prevents time/state leaks between chapters
const PaginatedChapterPageWrapper = () => {
  const { id } = useParams();
  return <PaginatedChapterPage key={id} />;
};
import KenHillCaseStudyPage from "@/pages/KenHillCaseStudyPage";
import FinalExamPage from "@/pages/FinalExamPage";
import AdminPortalPage from "@/pages/AdminPortalPage";
import CourseIntroductionPage from "@/pages/CourseIntroductionPage";
import PracticeTestPage from "@/pages/PracticeTestPage";
import CompliancePage from "@/pages/CompliancePage";
import CertificatePreviewPage from "@/pages/CertificatePreviewPage";
import MyRecordsPage from "@/pages/MyRecordsPage";
import IDCardPage from "@/pages/IDCardPage";
import VerifyPage from "@/pages/VerifyPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import PreLicensingDashboardPage from "@/pages/PreLicensingDashboardPage";
import PreLicensingChapterPage from "@/pages/PreLicensingChapterPage";
import PlFinalExamPage from "@/pages/PlFinalExamPage";

// Wrapper forces full remount when chapter ID changes for pre-licensing
const PreLicensingChapterPageWrapper = () => {
  const { id } = useParams();
  return <PreLicensingChapterPage key={id} />;
};

// Protected Route Component
const ProtectedRoute = ({ children, requirePaid = false, requireAdmin = false }) => {
  const { isAuthenticated, loading, hasPaid, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  if (requirePaid && !hasPaid && !isAdmin) {
    return <Navigate to="/enroll" replace />;
  }

  return children;
};

// Public Route Component (redirects if logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary">Loading...</div>
      </div>
    );
  }

  if (isAuthenticated) {
    if (isAdmin) {
      return <Navigate to="/admin" replace />;
    }
    // Authenticated non-admin users go to enrollment page to pick a course
    return <Navigate to="/enroll" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<MainLandingPage />} />
      <Route path="/course/bail-enforcement" element={<LandingPage />} />
      <Route path="/verify" element={<VerifyPage />} />
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <RegisterPage />
        </PublicRoute>
      } />
      <Route path="/forgot-password" element={
        <PublicRoute>
          <ForgotPasswordPage />
        </PublicRoute>
      } />
      <Route path="/reset-password" element={
        <PublicRoute>
          <ResetPasswordPage />
        </PublicRoute>
      } />

      {/* Auth Required Routes */}
      <Route path="/enroll" element={
        <ProtectedRoute>
          <EnrollPage />
        </ProtectedRoute>
      } />
      
      {/* Payment callback routes */}
      <Route path="/payment/success" element={
        <ProtectedRoute>
          <EnrollPage />
        </ProtectedRoute>
      } />
      <Route path="/payment/cancel" element={
        <ProtectedRoute>
          <EnrollPage />
        </ProtectedRoute>
      } />

      {/* Paid User Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute requirePaid>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/course-introduction" element={
        <ProtectedRoute requirePaid>
          <CourseIntroductionPage />
        </ProtectedRoute>
      } />
      <Route path="/chapters" element={
        <ProtectedRoute requirePaid>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/chapter/:id" element={
        <ProtectedRoute requirePaid>
          <PaginatedChapterPageWrapper />
        </ProtectedRoute>
      } />
      <Route path="/recovery-board" element={
        <ProtectedRoute requirePaid>
          <RecoveryBoardPage />
        </ProtectedRoute>
      } />
      <Route path="/case-study/ken-hill" element={
        <ProtectedRoute requirePaid>
          <KenHillCaseStudyPage />
        </ProtectedRoute>
      } />
      <Route path="/final-exam" element={
        <ProtectedRoute requirePaid>
          <FinalExamPage />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminPortalPage />
        </ProtectedRoute>
      } />
      <Route path="/practice-test" element={
        <ProtectedRoute requirePaid>
          <PracticeTestPage />
        </ProtectedRoute>
      } />
      <Route path="/compliance" element={<CompliancePage />} />
      <Route path="/certificate-preview" element={<CertificatePreviewPage />} />
      <Route path="/my-records" element={
        <ProtectedRoute>
          <MyRecordsPage />
        </ProtectedRoute>
      } />
      <Route path="/id-card" element={
        <ProtectedRoute requirePaid>
          <IDCardPage />
        </ProtectedRoute>
      } />

      {/* Pre-Licensing Course Routes */}
      <Route path="/pre-licensing/dashboard" element={
        <ProtectedRoute requirePaid>
          <PreLicensingDashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/pre-licensing/chapter/:id" element={
        <ProtectedRoute requirePaid>
          <PreLicensingChapterPageWrapper />
        </ProtectedRoute>
      } />
      <Route path="/pre-licensing/final-exam" element={
        <ProtectedRoute requirePaid>
          <PlFinalExamPage />
        </ProtectedRoute>
      } />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster 
          position="top-right"
          toastOptions={{
            style: {
              background: 'hsl(0 0% 7%)',
              border: '1px solid hsl(0 0% 20%)',
              color: 'hsl(0 0% 98%)'
            }
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
